import { Link } from "../components/LocaleLink";
import {
  ChevronRight, ArrowRight, MapPin, Languages, BadgeCheck, Newspaper, BookOpen,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CONTACT } from "../i18n/translations";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { JsonLd, buildBreadcrumbSchema } from "../components/JsonLd";

const origin = () =>
  typeof window !== "undefined" ? window.location.origin : "https://velmiraliving.com";

const KNOWS_ABOUT = [
  "French Riviera real estate market",
  "Short-term rental regulation in Nice (mairie registration, taxe de séjour)",
  "LMNP taxation for furnished rentals",
  "Property management operations & owner reporting",
  "Foreign buyer advisory (US, Turkish, UK buyers)",
  "Riviera concierge & guest experience",
];

const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

export default function AuthorGorkem() {
  const { lang, t } = useLang();
  const url = `${origin()}/${lang}/author/gorkem`;
  const breadcrumb = [
    { name: t("blogUi.home"), url: `${origin()}/${lang}` },
    { name: "Görkem — Author profile", url },
  ];

  return (
    <div data-testid="author-page" className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Görkem — Founder & Head of Operations | Velmira Living"
        description="Görkem is the founder of Velmira Living, a boutique real estate and property management house in Nice. He oversees 60+ managed Riviera properties and reviews every guide published on this site."
      />
      <JsonLd id="author-breadcrumb" data={buildBreadcrumbSchema(breadcrumb)} />
      <JsonLd
        id="author-person"
        data={{
          "@context": "https://schema.org",
          "@type": "Person",
          name: "Görkem",
          url: "https://velmiraliving.com/en/author/gorkem",
          jobTitle: "Founder & Head of Operations",
          worksFor: {
            "@type": "Organization",
            name: "Velmira Living",
            url: "https://velmiraliving.com",
          },
          image: "https://velmiraliving.com/team/gorkem.jpg",
          knowsAbout: KNOWS_ABOUT,
          knowsLanguage: ["en", "fr", "tr"],
          workLocation: { "@type": "Place", name: "Nice, France" },
          sameAs: [
            "https://www.linkedin.com/company/velmira",
            "https://www.instagram.com/velmiraliving",
            "https://velmiraliving.com/en/press",
          ],
          description:
            "Founder of Velmira Living, a boutique real estate and property management house on the French Riviera. Oversees a portfolio of 60+ managed properties for international owners and reviews all published guides for accuracy.",
        }}
      />

      <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[900px]">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-8">
          <Link to="/" className="hover:text-primary">{t("blogUi.home")}</Link>
          <ChevronRight size={12} />
          <span className="text-primary">Author</span>
        </nav>

        <Reveal>
          <div className="flex flex-col sm:flex-row gap-8 items-start">
            <div className="w-36 h-36 shrink-0 overflow-hidden border border-primary/30">
              <img src="/team/gorkem.jpg" alt="Görkem — Founder of Velmira Living" className="w-full h-full object-cover" />
            </div>
            <div>
              <p className={overline}>
                <BadgeCheck size={13} strokeWidth={1.6} className="inline mr-2 -mt-0.5" />
                Author & reviewer
              </p>
              <h1 className="mt-4 font-headings text-4xl sm:text-5xl leading-[1.05] tracking-tight text-foreground">
                Görkem
              </h1>
              <p lang="en" className="brand-caps mt-3 font-accent text-xs uppercase tracking-[0.25em] text-foreground/70">
                Founder & Head of Operations — Velmira Living
              </p>
              <div className="mt-4 flex flex-wrap gap-4 font-body text-sm text-foreground/60">
                <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-primary" /> Nice, France</span>
                <span className="inline-flex items-center gap-1.5"><Languages size={13} className="text-primary" /> EN · FR · TR</span>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-12 space-y-5 font-body font-light text-lg text-foreground/80 leading-relaxed max-w-3xl">
            <p>
              Görkem founded Velmira Living to give international owners what he could not find on
              the Riviera as an investor himself: one accountable, multilingual team handling
              acquisition, short-term rentals, maintenance and concierge without handovers.
            </p>
            <p>
              He leads day-to-day operations across a portfolio of <strong className="text-foreground">60+
              managed properties</strong> from Cannes to Menton, works directly with the Mairie de
              Nice registration and taxe de séjour processes, and structures LMNP-compliant rental
              operations for owners from the United States, Turkey, France and Italy.
            </p>
            <p>
              Every guide, data report and market article published on velmiraliving.com is
              reviewed by Görkem for factual accuracy before publication.
            </p>
          </div>
        </Reveal>

        <Reveal>
          <section className="mt-14">
            <p className={overline}>Expertise</p>
            <h2 className="mt-4 font-headings text-2xl sm:text-3xl text-foreground">Topics reviewed</h2>
            <ul className="mt-6 flex flex-wrap gap-2">
              {KNOWS_ABOUT.map((k) => (
                <li key={k} className="font-body text-sm border border-border/60 text-foreground/75 px-4 py-2">
                  {k}
                </li>
              ))}
            </ul>
          </section>
        </Reveal>

        <Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Link to="/blog" data-testid="author-articles-link" className="group border border-border/60 bg-secondary p-7 hover:border-primary transition-colors">
              <BookOpen size={20} className="text-primary" strokeWidth={1.5} />
              <p className="mt-4 font-headings text-xl text-foreground group-hover:text-primary transition-colors">Reviewed articles & guides</p>
              <p className="mt-2 font-body font-light text-sm text-foreground/70">All published insights on buying, renting and managing Riviera property.</p>
            </Link>
            <Link to="/press" data-testid="author-press-link" className="group border border-border/60 bg-secondary p-7 hover:border-primary transition-colors">
              <Newspaper size={20} className="text-primary" strokeWidth={1.5} />
              <p className="mt-4 font-headings text-xl text-foreground group-hover:text-primary transition-colors">Press & interviews</p>
              <p className="mt-2 font-body font-light text-sm text-foreground/70">Available for expert commentary in EN, FR, TR and ES — see the media kit.</p>
            </Link>
          </div>
        </Reveal>

        <Reveal>
          <div className="mt-14 border border-primary/30 bg-secondary p-10 text-center">
            <p className="font-headings text-2xl sm:text-3xl text-foreground">Have a question about a Riviera property?</p>
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="author-cta"
              className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all"
            >
              Message Görkem&rsquo;s team <ArrowRight size={15} />
            </a>
          </div>
        </Reveal>
      </div>
    </div>
  );
}
